import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableSortLabel,
  TableRow,
  Typography,
} from '@mui/material';
import { bytesToString, formatNumber } from 'js/lib/FormatUtils';
import { getMetricTotals, isZeroRow, MetricRow, SortDirection } from '../metricRows';
import styles from './MetricsTrendPanel.module.scss';

type MetricsTrendTableProps = {
  rows: MetricRow[];
  hideRecords?: boolean;
  sortDir: SortDirection;
  onSortChange: (direction: SortDirection) => void;
};

export const MetricsTrendTable = ({
  rows,
  hideRecords = false,
  sortDir,
  onSortChange,
}: MetricsTrendTableProps) => {
  const totals = getMetricTotals(rows);

  return (
    <TableContainer className={styles.tableContainer}>
      <Table size="small" aria-label="Resource metrics by day">
        <TableHead>
          <TableRow>
            <TableCell sortDirection={sortDir}>
              <TableSortLabel
                active
                direction={sortDir}
                onClick={() => onSortChange(sortDir === 'asc' ? 'desc' : 'asc')}
              >
                Date
              </TableSortLabel>
            </TableCell>
            {!hideRecords && <TableCell align="right">Records</TableCell>}
            <TableCell align="right">Errors</TableCell>
            <TableCell align="right">Data Volume</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.date}
              className={`${styles.dataRow} ${isZeroRow(row) ? styles.mutedRow : ''}`}
            >
              <TableCell>{row.date}</TableCell>
              {!hideRecords && <TableCell align="right">{formatNumber(row.records)}</TableCell>}
              <TableCell align="right" className={row.errors > 0 ? styles.errorValue : undefined}>
                {formatNumber(row.errors)}
              </TableCell>
              <TableCell align="right">{bytesToString(row.size)}</TableCell>
            </TableRow>
          ))}
        </TableBody>

        <TableFooter>
          <TableRow className={styles.totalRow}>
            <TableCell>
              <Typography className={styles.totalLabel}>Total</Typography>
            </TableCell>
            {!hideRecords && (
              <TableCell align="right">
                <Typography className={styles.totalLabel}>
                  {formatNumber(totals.records)}
                </Typography>
              </TableCell>
            )}
            <TableCell align="right">
              <Typography
                className={`${styles.totalLabel} ${totals.errors > 0 ? styles.errorValue : ''}`}
              >
                {formatNumber(totals.errors)}
              </Typography>
            </TableCell>
            <TableCell align="right">
              <Typography className={styles.totalLabel}>{bytesToString(totals.size)}</Typography>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

MetricsTrendTable.displayName = 'MetricsTrendTable';
